import { useState } from 'react';
import { Banknote, CalendarRange, X } from 'lucide-react';
import {
  formatCurrency,
  formatDateOnly,
  isPositiveAmount,
  type ExpenseItemWithRelations,
} from '@peles/shared';
import { useDialog } from '../../../context/DialogContext';
import { useRemoveExpensePayment } from '../../../hooks/useExpenses';
import { extractApiErrorMessage } from '../../../utils/apiError';
import { InstallmentEditor } from './InstallmentEditor';
import { MoneyStateBadge } from './MoneyStateBadge';
import { RecordPaymentSheet } from './RecordPaymentSheet';
import { ScheduleList } from './ScheduleList';

export interface LineMoneyPanelProps {
  item: ExpenseItemWithRelations;
  /** The event's day, `YYYY-MM-DD` — where a scheduled balance falls by default. */
  eventDay?: string | null;
}

/**
 * A booked line's money in one place — the expense editor and a need's Money
 * tab both show it: price, paid and what is left, the payment schedule (read,
 * or edited with `InstallmentEditor`), the payments made, and "Record payment".
 */
export function LineMoneyPanel({ item, eventDay }: LineMoneyPanelProps) {
  const [editing, setEditing] = useState(false);
  const [paying, setPaying] = useState(false);
  const removePayment = useRemoveExpensePayment();
  const { confirm, alert } = useDialog();
  const owed = isPositiveAmount(item.balance);

  const remove = async (paymentId: string) => {
    if (
      !(await confirm({
        title: 'Remove Payment',
        message: 'Remove this payment?',
        confirmLabel: 'Remove',
        variant: 'danger',
      }))
    ) {
      return;
    }
    removePayment.mutate(
      { expenseItemId: item.id, paymentId },
      {
        onError: (err) =>
          void alert({
            title: 'Payment not removed',
            message: extractApiErrorMessage(err, 'The payment could not be removed.'),
            variant: 'error',
          }),
      },
    );
  };

  const button =
    'min-h-11 sm:min-h-9 px-3 inline-flex items-center gap-1.5 text-sm rounded-lg disabled:opacity-50';

  return (
    <section aria-label="Payments" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          Payments <MoneyStateBadge money={item.money} />
        </h3>
        <button
          type="button"
          onClick={() => setPaying(true)}
          disabled={!owed}
          className={`${button} bg-primary-600 text-white hover:bg-primary-700`}
        >
          <Banknote size={16} /> Record payment
        </button>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-gray-50 rounded-md px-3 py-2">
          <dt className="text-xs text-gray-500">Total Price</dt>
          <dd className="text-sm font-medium text-gray-900">{formatCurrency(item.price)}</dd>
        </div>
        <div className="bg-gray-50 rounded-md px-3 py-2">
          <dt className="text-xs text-gray-500">Total Paid</dt>
          <dd className="text-sm font-medium text-gray-900">{formatCurrency(item.totalPaid)}</dd>
        </div>
        <div className="bg-gray-50 rounded-md px-3 py-2">
          <dt className="text-xs text-gray-500">Balance</dt>
          <dd className={`text-sm font-medium ${owed ? 'text-red-600' : 'text-green-600'}`}>
            {formatCurrency(item.balance)}
          </dd>
        </div>
      </dl>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Schedule</h4>
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className={`${button} border border-gray-300 text-gray-700 hover:bg-gray-50`}
            >
              <CalendarRange size={14} />
              {item.installments.length ? 'Edit schedule' : 'Set a schedule'}
            </button>
          )}
        </div>
        {editing ? (
          <InstallmentEditor
            item={item}
            eventDay={eventDay}
            onSaved={() => setEditing(false)}
            onCancel={() => setEditing(false)}
          />
        ) : item.installments.length ? (
          <ScheduleList money={item.money} />
        ) : (
          <p className="text-sm text-gray-500">
            {item.dueDate
              ? `One payment, due ${formatDateOnly(item.dueDate)}.`
              : 'One payment, no due date yet.'}
          </p>
        )}
      </div>

      {item.payments.length > 0 && (
        <ul className="space-y-2">
          {item.payments.map((payment) => (
            <li
              key={payment.id}
              className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 rounded-md"
            >
              <div className="min-w-0">
                <span className="text-sm font-medium text-gray-900">
                  {formatCurrency(payment.amount)}
                </span>
                <span className="text-xs text-gray-500 ml-2">{payment.paymentMethodName}</span>
                <span className="text-xs text-gray-400 ml-2">{formatDateOnly(payment.paidAt)}</span>
                {payment.notes && (
                  <span className="text-xs text-gray-400 ml-2">- {payment.notes}</span>
                )}
              </div>
              {payment.clientPaymentId ? (
                // Recorded from a client payment: it changes with that payment,
                // and the server refuses to delete it here.
                <span className="text-xs text-gray-500">From client payment</span>
              ) : (
                <button
                  onClick={() => void remove(payment.id)}
                  type="button"
                  className="min-h-11 min-w-11 flex items-center justify-center text-gray-400 hover:text-red-600"
                  aria-label="Remove payment"
                >
                  <X size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <RecordPaymentSheet item={paying ? item : null} onClose={() => setPaying(false)} />
    </section>
  );
}
